import styled from "styled-components";
import React, { useCallback, useEffect, useState } from "react";
import HomeMain from "./HomeMain";
import HomeBidInboxDemoSection from "./HomeBidInboxDemoSection";
import HomeProcurementPricingSection from "./HomeProcurementPricingSection";
import NewsletterSignup from "../../components/home/NewsletterSignup";
import IntakeModal from "../../components/IntakeModal";
import { useBreakpoints } from "../../lib/breakpoints";
import { trackEvent } from "../../helpers/mixpanel";
import { device } from "../../lib/breakpoints";
import { marketingSectionHeading } from "../../styles/marketingSectionTypography";
import SEO from "../../components/SEO";
import {
  faqSchema,
  softwareApplicationSchema,
  websiteSchema,
} from "../../lib/seoConfig";
import {
  BRAND_NAV_TAGLINE_BADGE,
  CLUMSE_OPEN_RETAIL_INTAKE_EVENT,
  HOME_PAGE_TITLE,
  HOME_META_DESCRIPTION,
  HOME_TWITTER_DESCRIPTION,
  HOME_OG_TITLE,
  HOME_OG_DESCRIPTION,
  HOME_OG_IMAGE_ALT,
  HOME_META_KEYWORDS,
} from "../../helpers/constants";
import FAQSection from "./FAQSection";
import { useLocation } from "react-router-dom";
import { Button } from "antd";

/** Visually hidden; keeps high-intent phrases in the DOM for crawlers without affecting layout. */
const HOME_SEO_HIDDEN_STYLE = {
  position: "absolute",
  width: 1,
  height: 1,
  padding: 0,
  margin: -1,
  overflow: "hidden",
  clip: "rect(0, 0, 0, 0)",
  whiteSpace: "nowrap",
  border: 0,
};

export default function Home() {
  const location = useLocation();
  const { isMobile } = useBreakpoints();
  const [showIntakeModal, setShowIntakeModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState("retail");
  const [heroIntakeProblem, setHeroIntakeProblem] = useState("");

  const openIntake = useCallback((opts = {}) => {
    if (opts.plan) setSelectedPlan(opts.plan);
    setHeroIntakeProblem(
      opts.heroIntakeProblem !== undefined ? opts.heroIntakeProblem : ""
    );
    setShowIntakeModal(true);
  }, []);

  const closeIntake = useCallback(() => {
    setShowIntakeModal(false);
    setSelectedPlan("retail");
    setHeroIntakeProblem("");
  }, []);

  useEffect(() => {
    const onRetailIntake = () => openIntake({ plan: "retail" });
    window.addEventListener(CLUMSE_OPEN_RETAIL_INTAKE_EVENT, onRetailIntake);
    return () =>
      window.removeEventListener(
        CLUMSE_OPEN_RETAIL_INTAKE_EVENT,
        onRetailIntake
      );
  }, [openIntake]);

  const handleIntakeSubmit = useCallback(async () => {
    try {
      setShowIntakeModal(false);
    } catch (error) {
      console.error("Error processing intake:", error);
    }
  }, []);

  // SPA hash links do not always trigger native anchor scroll on route changes.
  useEffect(() => {
    if (!location.hash) return;
    const id = location.hash.replace(/^#/, "");
    const target = document.getElementById(id);
    if (!target) return;
    const scrollToHashTarget = () => {
      // HomeMain uses a fixed industrial nav; leave room so headings aren’t hidden.
      const navOffset = window.innerWidth <= 768 ? 88 : 96;
      const y = target.getBoundingClientRect().top + window.scrollY - navOffset;
      window.scrollTo({ top: Math.max(0, y), behavior: "smooth" });
    };
    // Delay a tick to ensure section is rendered after client-side navigation.
    requestAnimationFrame(() => setTimeout(scrollToHashTarget, 0));
  }, [location.hash]);

  // Rich JSON-LD: wedge-first product copy + reviews (organization appended in SEO.js)
  const homeSoftwareSchema = {
    ...softwareApplicationSchema,
    description: HOME_META_DESCRIPTION,
    alternateName: [
      BRAND_NAV_TAGLINE_BADGE,
      "Clumse contractor bids",
      "Clumse AI",
      "maintenance and repair bidding",
    ],
    featureList: [
      "Clumse AI scopes jobs and keeps vendor threads organized",
      "Benchmark pricing so you know a fair range before you hire",
      "Side-by-side bids with clear scope and market baseline alignment",
      "Held payments and milestone releases where enabled",
    ],
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: "4.9",
      reviewCount: "17",
      bestRating: "5",
      worstRating: "1",
    },
    review: [
      {
        "@type": "Review",
        author: { "@type": "Person", name: "Small Portfolio Operator" },
        datePublished: "2025-09-15",
        reviewBody:
          "Our tenants finally stopped texting me at midnight for drips and policy questions. The magic link and Clumse AI handle the noise. Real emergencies still reach me.",
        name: "Finally apples-to-apples bids",
        reviewRating: {
          "@type": "Rating",
          ratingValue: "5",
          bestRating: "5",
          worstRating: "1",
        },
      },
    ],
  };

  const homeStructuredData = [homeSoftwareSchema, websiteSchema, faqSchema];

  return (
    <Container>
      <SEO
        title={HOME_PAGE_TITLE}
        description={HOME_META_DESCRIPTION}
        keywords={HOME_META_KEYWORDS}
        imageAlt={HOME_OG_IMAGE_ALT}
        ogTitle={HOME_OG_TITLE}
        ogDescription={HOME_OG_DESCRIPTION}
        twitterTitle={HOME_OG_TITLE}
        twitterDescription={HOME_TWITTER_DESCRIPTION}
        schema={homeStructuredData}
        category="Property maintenance and contractor bids"
        url="https://clumse.com/"
        canonical="https://clumse.com/"
        tags={[
          "Clumse",
          "vendor bids",
          "Clumse AI",
          "contractor quotes",
          "vetted contractors",
          "ZIP benchmarks",
        ]}
      />
      <p className="home-seo-context" style={HOME_SEO_HIDDEN_STYLE}>
        {BRAND_NAV_TAGLINE_BADGE}. Clumse: the easiest way for homeowners to
        manage repairs and remodeling with AI-scoped jobs and vetted local
        contractor bids.
      </p>
      <HomeMain openIntake={openIntake} />

      {/* Compliance Section */}
      {/*
 <ComplianceSection>
 <ComplianceContainer>
 <ComplianceIcon><SafetyCertificateOutlined /></ComplianceIcon>
 <ComplianceTitle>Simple. Smart. Investor-Grade.</ComplianceTitle>
 <ComplianceText>
 Ask Clumse AI compliance questions anytime. Get guidance on common operator-tenant issues and lease-related questions.
 </ComplianceText>
 <ComplianceFeatures>
 <ComplianceFeature>
 <CheckCircleOutlined /> Ask compliance questions anytime
 </ComplianceFeature>
 <ComplianceFeature>
 <CheckCircleOutlined /> Get guidance on common operator-tenant issues
 </ComplianceFeature>
 <ComplianceFeature>
 <CheckCircleOutlined /> Understand lease terms and requirements
 </ComplianceFeature>
 </ComplianceFeatures>
 </ComplianceContainer>
 </ComplianceSection>
 */}

      {/* Why Clumse Section */}
      {/*
 <WhySection>
 <WhyContainer>
 <WhyTitle>Reliable Support</WhyTitle>
 <WhySubtitle>
 Daily support availability. Get help through email.
 </WhySubtitle>
 <WhyComparison>
 <ComparisonColumn $old>
 <ComparisonTitle $old>Traditional Software</ComparisonTitle>
 <ComparisonItem $old>
 <span>❌</span> Multiple dashboards and logins
 </ComparisonItem>
 <ComparisonItem $old>
 <span>❌</span> Hours of training required
 </ComparisonItem>
 <ComparisonItem $old>
 <span>❌</span> Manual data entry and tracking
 </ComparisonItem>
 <ComparisonItem $old>
 <span>❌</span> You coordinate everything
 </ComparisonItem>
 </ComparisonColumn>
 
 <ComparisonColumn>
 <ComparisonTitle>Clumse with Clumse AI</ComparisonTitle>
 <ComparisonItem>
 <CheckCircleOutlined /> Just chat with Clumse AI
 </ComparisonItem>
 <ComparisonItem>
 <CheckCircleOutlined /> No training (works like texting)
 </ComparisonItem>
 <ComparisonItem>
 <CheckCircleOutlined /> Clumse AI tracks and acts automatically
 </ComparisonItem>
 <ComparisonItem>
 <CheckCircleOutlined /> Clumse AI helps coordinate requests
 </ComparisonItem>
 </ComparisonColumn>
 </WhyComparison>
 </WhyContainer>
 </WhySection>
 */}

      {/* Newsletter Signup Section */}
      <NewsletterSignup />

      <HomeBidInboxDemoSection />

      <HomeProcurementPricingSection
        onSelectPlan={(planId) => {
          setSelectedPlan(planId);
          setShowIntakeModal(true);
          trackEvent("Home pricing CTA to intake", {
            plan: planId,
            device_type: isMobile ? "mobile" : "desktop",
          });
        }}
      />

      {/* <SocialProofSection /> */}

      {/* FAQ Section */}
      <FAQSection />

      <IntakeModal
        isOpen={showIntakeModal}
        onClose={closeIntake}
        onSubmit={handleIntakeSubmit}
        selectedPlan={selectedPlan}
        heroIntakeProblem={heroIntakeProblem}
        marketingSignup
        pricingProduct="procurement"
      />

      {/* Leo Learning Section */}
      {/*
 <LeoLearningSection>
 <LeoLearningContainer>
 <LeoLearningIcon><img src="/icons/leo/leo.png" alt="Clumse AI" style={{ margin: "auto", width: '48px', height: '48px', filter: 'brightness(0) invert(1)' }}/></LeoLearningIcon>
 <LeoLearningTitle>Clumse AI Gets Smarter {isMobile && <br />} Every Day</LeoLearningTitle>
 <LeoLearningText>
 Clumse AI learns your style as you use it. Favorite vendors, communication preferences, even potential issues, Clumse AI gets smarter about your assets every day. It's like having an asset manager who's worked with you for years, from the start.
 </LeoLearningText>
 </LeoLearningContainer>
 </LeoLearningSection>
 */}
    </Container>
  );
}

const Container = styled.div`
  width: 100%;
  background: #000000;
  color: #cbd5e1;
  overflow-x: hidden;
  min-height: 100vh;
  position: relative;

  &::before {
    content: "";
    position: fixed;
    inset: 0;
    pointer-events: none;
    z-index: 0;
    opacity: 0.45;
    background-image: linear-gradient(
        rgba(10, 10, 10, 0.07) 0.5px,
        transparent 0.5px
      ),
      linear-gradient(90deg, rgba(10, 10, 10, 0.07) 0.5px, transparent 0.5px);
    background-size: 20px 20px;
  }

  /*
   * Lift sections above the fixed ::before mesh (z-index: 0).
   * Skip HomeMain (data-home-main): the global industrial nav lives outside this subtree.
   */
  > *:not([data-home-main]) {
    position: relative;
    z-index: 1;
  }

  @media ${device.mobile} {
    overflow-x: hidden;
  }
`;

const SignUpCtaContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem 2rem 5rem 2rem;
  background: var(--color-background);

  @media ${device.mobile} {
    padding: 1.5rem 1rem 3rem 1rem;
  }

  @media (max-width: 375px) {
    padding: 1rem 0.5rem 2rem 0.5rem;
  }
`;
const SectionHeading = styled.h2`
  font-size: 3.25rem;
  font-weight: 600;
  color: var(--color-text);
  text-align: center;
  margin-bottom: 1.5rem;
  font-family: "Inter", system-ui, ui-sans-serif, sans-serif;
  letter-spacing: -0.02em;

  @media (max-width: 768px) {
    font-size: 2.75rem;
  }
`;

const BenefitsSection = styled.section`
  background: linear-gradient(135deg, #f0f9f0 0%, #e8f5e8 100%);
  padding: 6rem 2rem;
  position: relative;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 100px;
    background: linear-gradient(
      180deg,
      var(--color-background) 0%,
      #f0f9f0 100%
    );
    pointer-events: none;
  }

  @media ${device.mobile} {
    padding: 5rem 1.5rem;
  }

  .anticon svg {
    width: 40px;
    height: 40px;
  }
`;

const BenefitsContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  position: relative;
  z-index: 2;
`;

const BenefitsTitle = styled.h2`
  font-size: 3.5rem;
  font-weight: 600;
  color: var(--color-text);
  text-align: center;
  margin-bottom: 1.5rem;
  font-family: var(--font-primary);
  letter-spacing: -0.02em;

  @media ${device.mobile} {
    font-size: 2.875rem;
  }
`;

const BenefitsSubtitle = styled.p`
  font-size: 1.375rem;
  line-height: 1.6;
  color: var(--color-text-light);
  text-align: center;
  margin-bottom: 4rem;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
  font-family: var(--font-secondary);
  letter-spacing: -0.005em;
  font-weight: 400;

  @media ${device.mobile} {
    font-size: 1.25rem;
  }
`;

const BenefitsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 2.5rem;

  @media ${device.mobile} {
    grid-template-columns: 1fr;
    gap: 2rem;
  }

  @media (max-width: 375px) {
    gap: 1.5rem;
  }
`;

const BenefitCard = styled.div`
  background: var(--color-bg-secondary);
  padding: 3rem 2.5rem;
  border-radius: 20px;
  text-align: center;
  box-shadow: 0 4px 20px var(--shadow-light);
  border: 1px solid var(--color-border-light);
  transition: box-shadow 0.3s cubic-bezier(0.4, 0, 0.2, 1),
    border-color 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;

  &.highlight {
    /* No highlight by default */
  }

  &:hover,
  &.highlight:hover {
    /* Show green glow and border only on hover */
    box-shadow: 0 0 0 6px rgba(44, 95, 45, 0.18),
      0 8px 32px 0 rgba(44, 95, 45, 0.18), 0 4px 20px var(--shadow-light);
    border-color: var(--color-primary-light);
    z-index: 2;
    /* Remove any transform, scale, or tilt */
    transform: none !important;
  }

  &::before {
    content: "";
    position: absolute;
    top: -50%;
    left: -50%;
    width: 200%;
    height: 200%;
    background: radial-gradient(
      circle,
      var(--color-primary-glow) 0%,
      transparent 70%
    );
    transition: opacity 0.3s ease;
    opacity: 0;
  }

  @media ${device.mobile} {
    padding: 2.5rem 2rem;
  }

  @media (max-width: 375px) {
    padding: 2rem 1.5rem;
  }
`;

const BenefitIcon = styled.div`
  font-size: 3.5rem;
  margin-bottom: 2rem;
  filter: grayscale(0.3) sepia(1) hue-rotate(120deg) saturate(0.8);
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  ${BenefitCard}:hover & {
    transform: scale(1.1);
  }
`;

const BenefitTitle = styled.h3`
  font-size: 1.75rem;
  font-weight: 600;
  color: var(--color-text);
  margin-bottom: 1.5rem;
  font-family: var(--font-primary);
  letter-spacing: -0.01em;
`;

const BenefitDescription = styled.p`
  font-size: 1.125rem;
  line-height: 1.6;
  color: var(--color-text-light);
  font-family: var(--font-secondary);
  margin: 0;
  letter-spacing: -0.005em;
  font-weight: 400;
`;

const FinalCTASection = styled.section`
  background: linear-gradient(120deg, #232016 0%, #4a7c59 40%, #181818 100%);
  padding: 7rem 2rem 8rem 2rem;
  position: relative;
  overflow: hidden;
  animation: fadeInScale 1s cubic-bezier(0.4, 0, 0.2, 1);

  &::before {
    content: "";
    position: absolute;
    inset: 0;
    background: rgba(24, 24, 24, 0.32);
    z-index: 1;
    pointer-events: none;
  }

  &::after {
    content: "";
    position: absolute;
    inset: 0;
    background: radial-gradient(
      ellipse at 60% 40%,
      rgba(255, 255, 255, 0.08) 0%,
      rgba(44, 95, 45, 0.04) 60%,
      rgba(24, 24, 24, 0.7) 100%
    );
    z-index: 2;
    pointer-events: none;
  }

  @media ${device.mobile} {
    padding: 5rem 1rem 6rem 1rem;
  }
`;

const FinalCTAContainer = styled.div`
  max-width: 820px;
  margin: 0 auto;
  text-align: center;
  position: relative;
  z-index: 3;
`;

const FinalCTATitle = styled.h2`
  font-size: 3.75rem;
  font-weight: 700;
  color: #fff;
  margin-bottom: 1.75rem;
  font-family: var(--font-stack-sans);
  letter-spacing: -0.03em;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
  animation: fadeInUp 1.1s cubic-bezier(0.4, 0, 0.2, 1);

  @media ${device.mobile} {
    font-size: 2.5rem;
    margin-bottom: 1.1rem;
  }
`;

const FinalCTASubtitle = styled.p`
  font-size: 1.5rem;
  line-height: 1.7;
  color: #f7f7f7;
  margin-bottom: 3.5rem;
  opacity: 0.97;
  font-family: "Inter", system-ui, ui-sans-serif, sans-serif;
  letter-spacing: -0.005em;
  font-weight: 400;
  text-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);
  animation: fadeInUp 1.2s cubic-bezier(0.4, 0, 0.2, 1);

  @media ${device.mobile} {
    font-size: 1.18rem;
    margin-bottom: 2.2rem;
  }
`;

const FinalCTAButtons = styled.div`
  display: flex;
  gap: 2rem;
  justify-content: center;
  align-items: center;
  margin-bottom: 2.8rem;
  animation: fadeInScale 1.3s cubic-bezier(0.4, 0, 0.2, 1);

  @media ${device.mobile} {
    flex-direction: column;
    gap: 1.25rem;
    margin-bottom: 2rem;
  }
`;

const FinalCTAPrimaryButton = styled.button`
  background: #fff;
  color: #4a7c59;
  padding: 1.3rem 3rem;
  border-radius: 999px;
  text-decoration: none;
  font-size: 1.18rem;
  font-weight: 700;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  display: inline-block;
  box-shadow: none;
  font-family: "Inter", system-ui, ui-sans-serif, sans-serif;
  position: relative;
  overflow: hidden;
  border: 1.5px solid #4a7c59;
  cursor: pointer;
  letter-spacing: -0.01em;

  &:hover {
    background: #f7f7f7;
    color: #2c5f2d;
    border-color: #2c5f2d;
    transform: scale(1.03);
  }

  @media ${device.mobile} {
    width: 100%;
    text-align: center;
    padding: 1.15rem 2rem;
    font-size: 1.05rem;
  }
`;

const FinalCTASecondaryButton = styled.a`
  background: transparent;
  color: #fff;
  padding: 1.3rem 3rem;
  border: 1.5px solid #fff;
  border-radius: 999px;
  text-decoration: none;
  font-size: 1.18rem;
  font-weight: 600;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  display: inline-block;
  font-family: "Inter", system-ui, ui-sans-serif, sans-serif;
  position: relative;
  overflow: hidden;
  letter-spacing: -0.01em;
  box-shadow: none;

  &:hover {
    color: #4a7c59;
    border-color: #4a7c59;
    background: #fff;
    transform: scale(1.03);
  }

  @media ${device.mobile} {
    width: 100%;
    text-align: center;
    padding: 1.15rem 2rem;
    font-size: 1.05rem;
  }
`;

const FinalCTATrust = styled.p`
  font-size: 1.08rem;
  color: var(--color-text-white);
  opacity: 0.97;
  margin: 0;
  font-family: "Inter", system-ui, ui-sans-serif, sans-serif;
  font-weight: 600;
  letter-spacing: -0.005em;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 1.5rem;
  margin-top: 2.5rem;
  text-shadow: 0 2px 8px #4a7c59;

  span {
    display: flex;
    align-items: center;
    gap: 0.4rem;
    font-size: 1.08rem;
    color: #fffbe6;
    font-weight: 700;
  }

  svg {
    margin-right: 0.4rem;
    color: #4a7c59;
    font-size: 1.18rem;
    vertical-align: middle;
  }

  @media ${device.mobile} {
    flex-direction: column;
    gap: 0.7rem;
    font-size: 0.98rem;
    margin-top: 1.5rem;
  }
`;

const SignUpCtaButton = styled.button`
  background: linear-gradient(90deg, #2c5f2d 0%, #4a7c59 100%);
  color: #fff;
  border: none;
  border-radius: 999px;
  padding: 1.15rem 2.5rem;
  font-size: 1.18rem;
  font-weight: 700;
  font-family: "Inter", system-ui, ui-sans-serif, sans-serif;
  letter-spacing: -0.01em;
  box-shadow: 0 4px 18px rgba(44, 95, 45, 0.13);
  cursor: pointer;
  margin-top: 1.5rem;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  &:hover {
    background: linear-gradient(90deg, #4a7c59 0%, #2c5f2d 100%);
    color: #fff;
    box-shadow: 0 8px 32px #4a7c59, 0 4px 16px rgba(44, 95, 45, 0.22);
    transform: scale(1.04);
  }
`;

const LuxuryCTASection = styled.div`
  background: linear-gradient(135deg, #f0f8f0 0%, #e8f5e8 100%);
  border: 2px solid #2c5f2d;
  border-radius: 24px;
  box-shadow: 0 8px 32px rgba(44, 95, 45, 0.1), 0 2px 8px rgba(0, 0, 0, 0.06);
  padding: 3rem 2rem 2.5rem 2rem;
  margin: 0 auto 2rem auto;
  max-width: 700px;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.5rem;
`;

const LuxuryCTATitle = styled.h2`
  font-family: var(--font-stack-sans);
  font-size: 2.5rem;
  font-weight: 700;
  color: #2c5f2d;
  margin-bottom: 0.5rem;
  letter-spacing: -0.02em;
`;

const LuxuryCTASubtitle = styled.p`
  font-family: "Inter", sans-serif;
  font-size: 1.25rem;
  color: #4a5568;
  margin-bottom: 1.5rem;
  opacity: 0.95;
  letter-spacing: -0.01em;
`;

const FeaturesSection = styled.section`
  padding: 6rem 2rem;
  max-width: 1200px;
  margin: 0 auto;

  @media ${device.mobile} {
    padding: 4rem 1rem;
  }
`;

const FeaturesHeader = styled.div`
  text-align: center;
  margin-bottom: 4rem;
`;

const FeaturesTitle = styled.h2`
  font-size: 3.5rem;
  font-weight: 700;
  color: var(--color-text);
  margin-bottom: 1rem;
  font-family: var(--font-primary);

  @media ${device.mobile} {
    font-size: 2.5rem;
  }
`;

const FeaturesSubtitle = styled.p`
  font-size: 1.3rem;
  color: var(--color-text-light);
  font-family: var(--font-secondary);
  line-height: 1.6;
  max-width: 800px;
  margin: 0 auto;

  @media ${device.mobile} {
    font-size: 1.1rem;
  }
`;

const FeaturesCTAButton = styled(Button)`
  background: #2c5f2d;
  color: white;
  border: 1.5px solid rgba(255, 255, 255, 0.8);
  padding: 1.15rem 2.7rem;
  font-size: 1.15rem;
  font-weight: 700;
  font-family: var(--font-secondary);
  border-radius: 999px;
  box-shadow: 0 8px 22px rgba(44, 95, 45, 0.4), 0 2px 6px rgba(0, 0, 0, 0.1);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  width: auto !important;
  margin: auto;
  position: relative;
  overflow: hidden;
  letter-spacing: -0.01em;
  min-width: 180px;

  &:hover {
    background: #4a7c59;
    border-color: rgba(255, 255, 255, 0.95);
    transform: translateY(-2px) scale(1.03);
    box-shadow: 0 14px 32px rgba(44, 95, 45, 0.5);
  }

  &:active {
    transform: scale(0.98);
  }

  @media ${device.mobile} {
    width: 100% !important;
    min-width: auto;
    font-size: 1.05rem;
    padding: 1.1rem 1.5rem;
  }
`;

// Alternating row background for feature sections (full-bleed)
const FeatureRow = styled.div`
  position: relative;
  padding: 2rem 0; /* vertical padding only; content spacing */

  /* Full-width background band */
  &::before {
    content: "";
    position: absolute;
    top: 0;
    bottom: 0;
    left: 50%;
    transform: translateX(-50%);
    width: 100vw;
    background: ${(props) =>
      props.$alt
        ? "linear-gradient(0deg, var(--color-primary-glow) 0%, rgba(44,95,45,0.06) 100%)"
        : "transparent"};
  }

  /* Ensure inner content sits above the background band */
  > * {
    position: relative;
    z-index: 1;
  }

  @media ${device.mobile} {
    margin-bottom: 3rem;
    padding: 1rem 0;
  }
`;

const FeatureCard = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 3rem;
  align-items: center;
  justify-items: center;
  margin-bottom: 1.5rem;
  margin-top: 1.5rem;
  min-height: 350px;

  ${(props) =>
    props.$reverse &&
    `
 direction: rtl;
 & > * {
 direction: ltr;
 }
 `}

  @media ${device.mobile} {
    grid-template-columns: 1fr;
    gap: 1.5rem;
    margin-bottom: 3rem;
    direction: ltr;
    min-height: auto;
  }
`;

const FeatureContent = styled.div`
  padding: 1.5rem 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  height: 100%;
  min-height: 250px;

  @media ${device.mobile} {
    min-height: auto;
  }
`;

const FeatureTitle = styled.h2`
  font-size: 2rem;
  font-weight: 700;
  color: var(--color-text);
  margin-bottom: 0.5rem;
  font-family: var(--font-primary);
`;

const FeatureHeadline = styled.h3`
  font-size: 1.5rem;
  font-weight: 600;
  color: var(--color-primary);
  margin-bottom: 1rem;
  font-family: var(--font-secondary);
`;

const FeatureDescription = styled.p`
  font-size: 1rem;
  color: var(--color-text-light);
  line-height: 1.5;
  margin-bottom: 1.5rem;
  font-family: var(--font-secondary);
`;

const BenefitsList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  margin-left: 1rem;
`;

const BenefitItem = styled.li`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 0.75rem;
  font-family: var(--font-secondary);
  color: var(--color-text);
  text-align: left;
  font-size: 0.95rem;
`;

const CheckIcon = styled.span`
  background: var(--color-primary);
  color: white;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.7rem;
  font-weight: bold;
  flex-shrink: 0;
`;

const FeatureImageContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  min-height: 250px;

  @media ${device.mobile} {
    min-height: auto;
  }
`;

const FeatureImage = styled.img`
  width: 100%;
  max-width: 450px;
  height: 250px;
  object-fit: cover;
  border-radius: 16px;
  border: 1px solid var(--color-border-green);
  box-shadow: var(--shadow-medium);
  transition: all 0.3s ease;

  &:hover {
    transform: scale(1.02);
    box-shadow: var(--shadow-dark);
  }
`;

const FeatureImagePlaceholder = styled.div`
  width: 100%;
  max-width: 450px;
  height: 250px;
  background: linear-gradient(
    135deg,
    var(--color-primary-glow) 0%,
    var(--color-bg-secondary) 100%
  );
  border-radius: 16px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  border: 1px solid var(--color-border-green);
  box-shadow: var(--shadow-medium);
`;

const ImageIcon = styled.div`
  font-size: 3rem;
  margin-bottom: 0.75rem;
  opacity: 0.7;
`;

const ImageText = styled.p`
  font-size: 1rem;
  color: var(--color-text-light);
  font-family: var(--font-secondary);
  margin: 0;
`;

// --- Feature Demo Mockups ---
const CardShell = styled.div`
  background: var(--color-bg-secondary);
  border: 1px solid var(--color-border-light);
  border-radius: 12px;
  box-shadow: var(--shadow-light);
  padding: 1rem;
  width: 100%;
  max-width: 500px;
`;

const DemoHeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-weight: 700;
  color: #1f2937;
  margin-bottom: 1.25rem;
  padding-bottom: 1rem;
  border-bottom: 2px solid #e5e7eb;

  @media ${device.mobile} {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
    margin-bottom: 1rem;
    padding-bottom: 0.75rem;
  }
`;

const HeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  color: var(--color-text-light);
  font-weight: 500;
  font-size: 0.9rem;
`;
const HeaderStat = styled.span`
  strong {
    color: var(--color-primary);
    font-weight: 700;
  }
`;
const HeaderDivider = styled.span`
  width: 1px;
  height: 16px;
  background: var(--color-border-light);
  display: inline-block;
`;

const DemoTable = styled.table`
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  font-size: 0.9rem;
  background: #f8fafc;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  min-width: 600px; /* Ensure table has minimum width for proper display */

  th,
  td {
    padding: 1rem 0.75rem;
    text-align: left;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  thead th {
    background: #1f2937;
    color: white;
    font-weight: 600;
    font-size: 0.75rem;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    border-bottom: none;
    padding: 1.25rem 0.75rem 0.75rem;
    white-space: nowrap;
  }

  tbody tr {
    background: white;
    border-bottom: 1px solid #e5e7eb;
    transition: all 0.2s ease;

    &:hover {
      background: #f9fafb;
      transform: translateY(-1px);
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    }

    &:last-child {
      border-bottom: none;
    }
  }

  tbody td {
    color: #374151;
    font-weight: 500;
    padding: 1rem 0.75rem;
    white-space: nowrap;
  }

  .align-right {
    text-align: right;
    font-weight: 600;
    color: #059669;
    white-space: nowrap;
  }

  @media ${device.mobile} {
    font-size: 0.8rem;
    min-width: 500px;

    th,
    td {
      padding: 0.75rem 0.5rem;
    }

    thead th {
      padding: 1rem 0.5rem 0.5rem;
      font-size: 0.7rem;
    }
  }
`;

const RentDemo = styled(CardShell)`
  max-width: 700px;
  padding: 1.5rem;
  background: white;
  border: 1px solid #e2e8f0;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1),
    0 2px 4px -1px rgba(0, 0, 0, 0.06);

  @media ${device.mobile} {
    padding: 1rem;
    max-width: 100%;
  }
`;

const DesktopTable = styled.div`
  display: block;

  @media ${device.mobile} {
    display: none;
  }
`;

const MobileCards = styled.div`
  display: none;

  @media ${device.mobile} {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    padding: 0.5rem 0;
  }
`;

const PaymentCard = styled.div`
  background: linear-gradient(135deg, #ffffff 0%, #f8fafc 50%, #f1f5f9 100%);
  border-radius: 16px;
  border: 2px solid
    ${(props) => {
      switch (props.$status) {
        case "paid":
          return "#d9f7be";
        case "pending":
          return "#fff7e6";
        case "overdue":
          return "#ffccc7";
        default:
          return "#e2e8f0";
      }
    }};
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  overflow: hidden;
  transition: all 0.3s ease;
  position: relative;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  }
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1.25rem 1.5rem 1rem;
  border-bottom: 1px solid #e2e8f0;
`;

const StatusIndicator = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${(props) => {
    switch (props.$status) {
      case "paid":
        return "linear-gradient(135deg, #52c41a 0%, #73d13d 100%)";
      case "pending":
        return "linear-gradient(135deg, #faad14 0%, #ffc53d 100%)";
      case "overdue":
        return "linear-gradient(135deg, #ff4d4f 0%, #ff7875 100%)";
      default:
        return "linear-gradient(135deg, #d9d9d9 0%, #f0f0f0 100%)";
    }
  }};
  color: white;
  font-size: 1.25rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
`;

const CardTitle = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const TenantInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: var(--color-primary);
  font-weight: 600;
  font-size: 1rem;
  transition: all 0.2s ease;

  .anticon {
    font-size: 0.9rem;
  }
`;

const UnitInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #6b7280;
  font-size: 0.875rem;
  font-weight: 500;

  .anticon {
    font-size: 0.8rem;
  }
`;

const CardContent = styled.div`
  padding: 1rem 1.5rem 1.5rem;
`;

const AmountSection = styled.div`
  text-align: center;
  margin-bottom: 1.5rem;
  padding: 1rem;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border-radius: 12px;
  border: 1px solid #e2e8f0;
`;

const AmountValue = styled.div`
  font-size: 2rem;
  font-weight: 800;
  color: var(--color-primary);
  line-height: 1;
  margin-bottom: 0.25rem;
`;

const AmountLabel = styled.div`
  font-size: 0.875rem;
  color: #6b7280;
  font-weight: 500;
`;

const DetailsSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const DetailRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem 0;
  border-bottom: 1px solid #f1f5f9;

  &:last-child {
    border-bottom: none;
  }
`;

const DetailLabel = styled.div`
  font-size: 0.875rem;
  color: #6b7280;
  font-weight: 500;
`;

const DetailValue = styled.div`
  font-size: 0.875rem;
  color: #1f2937;
  font-weight: 600;
`;

const StatusTag = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 600;
  background: ${(props) => props.color}15;
  color: ${(props) => props.color};
  border: 1px solid ${(props) => props.color}30;
`;

const MaintenanceDemo = styled(CardShell)``;

const Kanban = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0.75rem;

  @media ${device.mobile} {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
`;
const Column = styled.div`
  background: var(--color-bg-tertiary);
  border: 1px solid var(--color-border-light);
  border-radius: 10px;
  padding: 0.5rem;

  @media ${device.mobile} {
    padding: 0.75rem;
  }
`;
const ColTitle = styled.div`
  font-weight: 700;
  color: var(--color-text);
  margin-bottom: 0.5rem;
  font-size: 0.9rem;
`;
const Ticket = styled.div`
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 0.75rem;
  margin-bottom: 0.75rem;
  font-size: 0.85rem;
  color: #374151;
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  transition: all 0.2s ease;

  &:hover {
    border-color: var(--color-primary);
    box-shadow: 0 2px 8px rgba(44, 95, 45, 0.1);
    transform: translateY(-1px);
  }

  @media ${device.mobile} {
    padding: 1rem;
    gap: 1rem;
    margin-bottom: 1rem;
  }
`;

const TicketIcon = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--color-primary);
  color: white;
  font-size: 14px;
  flex-shrink: 0;

  svg {
    color: white;
    filter: brightness(0) invert(1);
  }

  @media ${device.mobile} {
    width: 40px;
    height: 40px;
    font-size: 16px;
  }
`;

const TicketContent = styled.div`
  flex: 1;
  min-width: 0;
`;

const TicketTitle = styled.div`
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 0.25rem;

  @media ${device.mobile} {
    font-size: 1rem;
    margin-bottom: 0.5rem;
  }
`;

const TicketUnit = styled.div`
  font-size: 0.75rem;
  color: #6b7280;
  font-weight: 500;

  @media ${device.mobile} {
    font-size: 0.875rem;
  }
`;

const CommsDemo = styled(CardShell)`
  max-width: 500px;
  padding: 0;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
`;

const Thread = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  font-size: 0.85rem;
  padding: 1.25rem;
  background: white;
  border-radius: 12px;
  margin: 0.75rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`;
const Msg = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  padding: 0.75rem 1rem;
  border-radius: 16px;
  background: ${(p) =>
    p.$ai ? "linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)" : "#f8fafc"};
  border: 1px solid ${(p) => (p.$ai ? "#bfdbfe" : "#e2e8f0")};
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  position: relative;
  margin-left: ${(p) => (p.$ai ? "0" : "1.5rem")};
  margin-right: ${(p) => (p.$ai ? "1.5rem" : "0")};

  &::before {
    content: "";
    position: absolute;
    ${(p) => (p.$ai ? "left: -6px;" : "right: -6px;")}
    top: 12px;
    width: 0;
    height: 0;
    border-top: 5px solid transparent;
    border-bottom: 5px solid transparent;
    ${(p) =>
      p.$ai
        ? "border-right: 6px solid #e0f2fe;"
        : "border-left: 6px solid #f8fafc;"}
  }

  b {
    font-weight: 600;
    color: ${(p) => (p.$ai ? "#1e40af" : "#374151")};
    font-size: 0.8rem;
    margin-bottom: 0.25rem;
  }
`;

const LeaseDemo = styled(CardShell)``;
const LeaseCard = styled.div`
  h4 {
    margin: 0 0 0.5rem 0;
    font-size: 1rem;
    color: var(--color-text);
  }
  ul {
    margin: 0;
    padding-left: 1rem;
    color: var(--color-text-light);
  }
`;
const LeaseActions = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-top: 0.75rem;
  button {
    background: var(--color-bg-secondary);
    border: 1px solid var(--color-primary);
    color: var(--color-primary);
    padding: 0.4rem 0.6rem;
    border-radius: 8px;
    font-weight: 600;
    cursor: default;
  }
`;

const PhotoStub = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 0.5rem;
  padding: 0.5rem;
  background: rgba(255, 255, 255, 0.7);
  border-radius: 12px;
  border: 1px solid #e2e8f0;

  .preview {
    width: 120px;
    height: 90px;
    border-radius: 8px;
    border: 1px solid #d1d5db;
    background: center/cover no-repeat url("/images/maintenance/leak.jpg");
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    transition: all 0.2s ease;
    cursor: pointer;

    &:hover {
      transform: scale(1.05);
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    }
  }
`;

const PhotoText = styled.div`
  font-size: 0.85rem;
  color: var(--color-text-light);
`;

const MoreFeaturesDemo = styled(CardShell)`
  max-width: 500px;
  text-align: center;
`;

const FeaturesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;
  margin-bottom: 1rem;
`;

const FeatureItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  padding: 1rem 0.5rem;
  background: var(--color-bg-tertiary);
  border: 1px solid var(--color-border-light);
  border-radius: 12px;
  transition: all 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: var(--shadow-light);
    border-color: var(--color-primary-light);
  }
`;

const FeatureIcon = styled.div`
  font-size: 1.5rem;
  margin-bottom: 0.25rem;
  color: var(--color-primary);
  display: flex;
  align-items: center;
  justify-content: center;

  .anticon {
    font-size: 1.5rem;
    color: var(--color-primary);
  }
`;

const FeatureName = styled.div`
  font-size: 0.8rem;
  font-weight: 600;
  color: var(--color-text);
  text-align: center;
  line-height: 1.2;
`;

const MoreFeaturesText = styled.div`
  font-size: 0.9rem;
  color: var(--color-text-light);
  font-style: italic;
  margin-top: 0.5rem;
`;

const StatusPill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.4rem 0.8rem;
  border-radius: 999px;
  font-weight: 600;
  font-size: 0.7rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: ${(p) =>
    p.$status === "paid"
      ? "#065f46"
      : p.$status === "pending"
      ? "#92400e"
      : "#991b1b"};
  background: ${(p) =>
    p.$status === "paid"
      ? "#d1fae5"
      : p.$status === "pending"
      ? "#fef3c7"
      : "#fee2e2"};
  border: 1px solid
    ${(p) =>
      p.$status === "paid"
        ? "#a7f3d0"
        : p.$status === "pending"
        ? "#fde68a"
        : "#fecaca"};
`;

const MethodPill = styled.span`
  display: inline-block;
  padding: 0.3rem 0.6rem;
  border-radius: 6px;
  font-weight: 600;
  font-size: 0.7rem;
  color: #374151;
  background: #f3f4f6;
  border: 1px solid #d1d5db;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const ScreeningDemo = styled(CardShell)``;
const ScoreRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.75rem;
`;
const Score = styled.div`
  font-weight: 800;
  color: var(--color-primary);
`;
const Badges = styled.div`
  display: flex;
  gap: 0.4rem;
  font-size: 0.75rem;
  span {
    padding: 0.15rem 0.5rem;
    border-radius: 999px;
    border: 1px solid var(--color-border-light);
  }
  .ok {
    background: rgba(16, 185, 129, 0.12);
    color: var(--color-success);
    border-color: rgba(16, 185, 129, 0.35);
  }
  .warn {
    background: rgba(245, 158, 11, 0.12);
    color: #b45309;
    border-color: rgba(245, 158, 11, 0.35);
  }
`;
const MiniGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0.5rem;
`;
const MiniCard = styled.div`
  background: var(--color-bg-tertiary);
  border: 1px solid var(--color-border-light);
  border-radius: 10px;
  padding: 0.5rem;
  label {
    display: block;
    font-size: 0.75rem;
    color: var(--color-text-muted);
  }
  value {
    display: block;
    font-size: 0.95rem;
    color: var(--color-text);
    font-weight: 600;
  }
`;

// Chat demo (AI Assistant)
const ChatDemoContainer = styled(CardShell)`
  max-width: 500px;
`;
const ChatHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid var(--color-border-light);
  font-weight: 700;
  color: var(--color-text);
`;
const ChatStatus = styled.span`
  font-size: 0.75rem;
  color: var(--color-success);
`;
const ChatWindow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding: 0.75rem 0;
  max-height: 280px;
  overflow-y: auto;
  overflow-x: hidden;

  @media ${device.mobile} {
    max-height: 200px;
    padding: 0.5rem 0;
  }

  /* Custom scrollbar styling */
  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: var(--color-border-light);
    border-radius: 2px;

    &:hover {
      background: var(--color-text-light);
    }
  }
`;
const ChatBubble = styled.div`
  align-self: ${(p) => (p.$ai ? "flex-start" : "flex-end")};
  background: ${(p) =>
    p.$ai ? "var(--color-primary-glow)" : "var(--color-primary)"};
  color: ${(p) => (p.$ai ? "var(--color-text)" : "var(--color-text-white)")};
  border: 1px solid var(--color-border-light);
  border-radius: 14px;
  padding: 0.5rem 0.75rem;
  max-width: 80%;
  font-size: 0.9rem;
`;
const ChatInput = styled.div`
  display: flex;
  gap: 0.5rem;
  border-top: 1px solid var(--color-border-light);
  padding-top: 0.5rem;
  input {
    flex: 1;
    border: 1px solid var(--color-border-light);
    border-radius: 10px;
    padding: 0.45rem 0.6rem;
  }
  button {
    background: var(--gradient-primary);
    color: var(--color-text-white);
    border: none;
    border-radius: 10px;
    padding: 0.45rem 0.8rem;
    font-weight: 600;
  }
`;

// Clumse AI Digest Email Demo Styled Components
const LeoEmailDemo = styled.div`
  background: var(--color-bg-secondary);
  border-radius: 16px;
  box-shadow: 0 8px 24px var(--shadow-light);
  max-width: 600px;
  margin: 0 auto;
  overflow: hidden;
  font-family: var(--font-primary);

  @media ${device.mobile} {
    max-width: 100%;
    margin: 0 1rem;
  }
`;

const LeoEmailHeader = styled.div`
  background: var(--gradient-primary);
  padding: 2.25rem 1.5rem;
  text-align: center;
  color: white;
  position: relative;

  &::after {
    content: "";
    position: absolute;
    inset: 0;
    background: radial-gradient(
      80% 140% at 50% -20%,
      rgba(255, 255, 255, 0.18) 0%,
      rgba(255, 255, 255, 0) 60%
    );
    pointer-events: none;
  }

  h1,
  h2,
  h3,
  h4,
  h5,
  h6,
  span,
  div {
    color: white !important;
  }
`;

const LeoIcon = styled.div`
  width: 56px;
  height: 56px;
  background: var(--color-primary-glow);
  border: 1px solid var(--color-border-green);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.6rem;
  margin: 0 auto 1rem;
  color: white;

  .anticon {
    color: white !important;
    filter: brightness(0) invert(1);
  }
`;

const LeoEmailTitle = styled.h1`
  font-size: 1.5rem;
  font-weight: 700;
  margin: 0 0 0.5rem 0;
  font-family: var(--font-primary);
  letter-spacing: -0.02em;
`;

const LeoEmailSubtitle = styled.p`
  font-size: 1rem;
  opacity: 0.95;
  margin: 0;
  font-family: var(--font-secondary, var(--font-primary));
  color: white;
`;

const LeoEmailBody = styled.div`
  padding: 0 0 1rem 0;
`;

const LeoGreeting = styled.div`
  font-size: 1.05rem;
  font-weight: 600;
  margin: 1rem 1.5rem 0.25rem 1.5rem;
  color: #1f2937;
`;

const LeoIntro = styled.div`
  font-size: 0.95rem;
  margin: 0 1.5rem 1rem 1.5rem;
  color: #6b7280;
  line-height: 1.5;
`;

const SummaryCard = styled.div`
  background: linear-gradient(
    180deg,
    rgba(44, 95, 45, 0.06) 0%,
    var(--color-bg-secondary) 70%
  );
  margin: 0 1.5rem 1rem 1.5rem;
  padding: 1.1rem;
  border-radius: 16px;
  border: 1px solid var(--color-border-green);
  box-shadow: 0 6px 18px rgba(44, 95, 45, 0.1);
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0.75rem;
  align-items: stretch;

  @media ${device.mobile} {
    grid-template-columns: 1fr;
    gap: 0.75rem;
  }
`;

const StatItem = styled.div`
  text-align: center;
  padding: 1.1rem 1rem;
  background: var(--color-bg-secondary);
  border-radius: 12px;
  border: 1px solid var(--color-border-light);
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.08);
`;

const StatNumber = styled.div`
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--color-primary);
  margin-bottom: 0.25rem;
`;

const StatLabel = styled.div`
  font-size: 0.8rem;
  color: #6b7280;
  font-weight: 500;
`;

const ActionDetails = styled.div`
  padding: 0 1.5rem 1.5rem;
`;

const ActionSectionTitle = styled.h2`
  font-size: 1.2rem;
  font-weight: 600;
  color: var(--color-text);
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const ActionCard = styled.div`
  background: var(--color-bg-secondary);
  border: 1px solid var(--color-border-light);
  border-radius: 8px;
  padding: 0.85rem 1rem;
  margin-bottom: 0.6rem;
  transition: all 0.2s ease;

  &:hover {
    border-color: var(--color-primary);
    box-shadow: 0 2px 12px var(--shadow-light);
  }
`;

const ActionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
`;

const ActionTitle = styled.h3`
  font-size: 0.95rem;
  font-weight: 600;
  color: #1f2937;
  margin: 0;
  flex: 1;
`;

const StatusBadge = styled.span`
  padding: 0.25rem 0.5rem;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 500;
  background: ${(props) => {
    switch (props.status) {
      case "completed":
        return "rgba(16, 185, 129, 0.15)";
      case "in_progress":
        return "rgba(234, 179, 8, 0.18)";
      case "pending":
        return "rgba(148, 163, 184, 0.18)";
      default:
        return "#F3F4F6";
    }
  }};
  color: ${(props) => {
    switch (props.status) {
      case "completed":
        return "var(--color-success)";
      case "in_progress":
        return "#92400E";
      case "pending":
        return "#6B7280";
      default:
        return "#6B7280";
    }
  }};
`;

const ActionDescription = styled.p`
  font-size: 0.85rem;
  color: #6b7280;
  margin: 0 0 0.5rem 0;
  line-height: 1.4;
`;

const ActionMeta = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 0.75rem;
  color: var(--color-text-muted);
`;

const TimeSaved = styled.span`
  background: rgba(16, 185, 129, 0.15);
  color: var(--color-success);
  padding: 0.25rem 0.5rem;
  border-radius: 999px;
  font-weight: 700;
  letter-spacing: -0.01em;
`;

const CTASection = styled.div`
  padding: 1.5rem;
  background: var(--color-bg-tertiary);
  border-top: 1px solid var(--color-border-light);
  display: flex;
  gap: 1rem;
  justify-content: center;

  @media ${device.mobile} {
    flex-direction: column;
  }
`;

const PrimaryButton = styled.button`
  background: var(--gradient-primary);
  color: var(--color-text-white);
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px var(--shadow-medium);
  }
`;

const SecondaryButton = styled.button`
  background: transparent;
  color: var(--color-primary);
  border: 1px solid var(--color-primary);
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: var(--color-primary);
    color: var(--color-text-white);
  }
`;

const EmailFooter = styled.div`
  padding: 1rem 1.5rem;
  background: var(--color-bg-tertiary);
  border-top: 1px solid var(--color-border-light);
  text-align: center;
  font-size: 0.75rem;
  color: var(--color-text-light);
`;

const UnsubscribeLink = styled.a`
  color: var(--color-text-light);
  text-decoration: underline;

  &:hover {
    color: var(--color-primary);
  }
`;

// Compliance Section Styles
const ComplianceSection = styled.section`
  padding: 6rem 2rem;
  background: linear-gradient(135deg, #f0f9f0 0%, #e8f5e8 100%);

  @media ${device.mobile} {
    padding: 5rem 1.5rem;
  }
`;

const ComplianceContainer = styled.div`
  max-width: 900px;
  margin: 0 auto;
  text-align: center;
`;

const ComplianceIcon = styled.div`
  font-size: 4rem;
  color: var(--color-primary);
  margin-bottom: 2rem;

  .anticon {
    font-size: 4rem;
  }

  @media ${device.mobile} {
    font-size: 3rem;

    .anticon {
      font-size: 3rem;
    }
  }
`;

const ComplianceTitle = styled.h2`
  font-size: 3.5rem;
  font-weight: 700;
  color: var(--color-text);
  margin-bottom: 2rem;
  font-family: var(--font-stack-sans);
  letter-spacing: -0.03em;

  @media ${device.mobile} {
    font-size: 2.5rem;
  }
`;

const ComplianceText = styled.p`
  font-size: 1.375rem;
  line-height: 1.7;
  color: var(--color-text-light);
  font-family: var(--font-secondary);
  max-width: 800px;
  margin: 0 auto 3rem;

  @media ${device.mobile} {
    font-size: 1.2rem;
    margin-bottom: 2rem;
  }
`;

const ComplianceFeatures = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  max-width: 600px;
  margin: 0 auto;
  align-items: center;
`;

const ComplianceFeature = styled.div`
  font-size: 1.25rem;
  color: var(--color-text);
  font-family: var(--font-secondary);
  display: flex;
  align-items: center;
  gap: 1rem;
  font-weight: 500;

  .anticon {
    color: var(--color-primary);
    font-size: 1.5rem;
  }

  @media ${device.mobile} {
    font-size: 1.1rem;
    gap: 0.75rem;

    .anticon {
      font-size: 1.25rem;
    }
  }
`;

// Why Section Styles
const WhySection = styled.section`
  padding: 6rem 2rem;
  background: var(--color-bg-secondary);

  @media ${device.mobile} {
    padding: 5rem 1.5rem;
  }
`;

const WhyContainer = styled.div`
  max-width: 1100px;
  margin: 0 auto;
`;

const WhyTitle = styled.h2`
  font-size: 3.5rem;
  font-weight: 700;
  color: var(--color-text);
  text-align: center;
  margin-bottom: 1rem;
  font-family: var(--font-stack-sans);
  letter-spacing: -0.03em;

  @media ${device.mobile} {
    font-size: 2.5rem;
  }
`;

const WhySubtitle = styled.p`
  font-size: 1.5rem;
  color: var(--color-primary);
  text-align: center;
  margin-bottom: 4rem;
  font-family: var(--font-secondary);
  font-weight: 600;

  .mobile-break {
    display: none;
  }

  @media ${device.mobile} {
    font-size: 1.25rem;
    margin-bottom: 3rem;

    .mobile-break {
      display: inline;
    }
  }
`;

const WhyComparison = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 3rem;

  @media ${device.mobile} {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

const ComparisonColumn = styled.div`
  background: ${(props) =>
    props.$old
      ? "#f3f4f6"
      : "linear-gradient(135deg, #f0f9f0 0%, #e8f5e8 100%)"};
  border: 2px solid
    ${(props) => (props.$old ? "#e5e7eb" : "var(--color-primary)")};
  border-radius: 16px;
  padding: 2rem;
  text-align: left;

  @media ${device.mobile} {
    padding: 1.5rem;
  }
`;

const ComparisonTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 700;
  color: ${(props) => (props.$old ? "#6c757d" : "var(--color-primary)")};
  margin-bottom: 2rem;
  text-align: center;
  font-family: var(--font-primary);

  @media ${device.mobile} {
    font-size: 1.25rem;
    margin-bottom: 1.5rem;
  }
`;

const ComparisonItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  margin-bottom: 1.5rem;
  font-size: 1.125rem;
  color: var(--color-text);
  font-family: var(--font-secondary);
  line-height: 1.5;

  span {
    font-size: 1.25rem;
    flex-shrink: 0;
    margin-top: 0.125rem;
  }

  .anticon {
    color: var(--color-primary);
    font-size: 1.25rem;
    flex-shrink: 0;
    margin-top: 0.125rem;
  }

  @media ${device.mobile} {
    font-size: 1rem;
    gap: 0.75rem;
    margin-bottom: 1.25rem;
  }
`;

// Leo Learning Section Styles
const LeoLearningSection = styled.section`
  padding: 5rem 2rem;
  background: linear-gradient(135deg, #1a3a2e 0%, #2c5f2d 100%);
  border-top: 2px solid var(--color-primary);
  position: relative;

  &::before {
    content: "";
    position: absolute;
    inset: 0;
    background: radial-gradient(
      ellipse at 50% 30%,
      rgba(74, 124, 89, 0.2) 0%,
      transparent 70%
    );
    pointer-events: none;
  }

  @media ${device.mobile} {
    padding: 4rem 1.5rem;
  }
`;

const LeoLearningContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
  text-align: center;
  position: relative;
  z-index: 1;
`;

const LeoLearningIcon = styled.div`
  font-size: 3rem;
  color: #a8e6a1;
  margin-bottom: 1.5rem;
  filter: none;

  .anticon {
    font-size: 3rem;
  }

  @media ${device.mobile} {
    font-size: 2.5rem;

    .anticon {
      font-size: 2.5rem;
    }
  }
`;

const LeoLearningTitle = styled.h2`
  font-size: 2.5rem;
  font-weight: 700;
  color: #ffffff;
  margin-bottom: 1.5rem;
  font-family: var(--font-stack-sans);
  letter-spacing: -0.02em;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);

  @media ${device.mobile} {
    font-size: 2rem;
  }
`;

const LeoLearningText = styled.p`
  font-size: 1.25rem;
  line-height: 1.7;
  color: rgba(255, 255, 255, 0.95);
  font-family: var(--font-secondary);
  max-width: 700px;
  margin: 0 auto;
  text-shadow: 0 1px 4px rgba(0, 0, 0, 0.15);

  @media ${device.mobile} {
    font-size: 1.1rem;
  }
`;
